import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import CryptoJS from "crypto-js";

import VerticalRegistry from "../../constants/verticalRegistry";
import SecondaryButton from "../../widgets/Buttons/SecondaryButton";

import StudentInfoCard from "../../components/StudentDetails/ActionCards/StudentInfo";
import CallManagementCard from "../../components/StudentDetails/ActionCards/CallManagement";
import OtpManagementCard from "../../components/StudentDetails/ActionCards/OtpManagement";
import OrderManagementCard from "../../components/StudentDetails/ActionCards/OrderManagement";
import MultipleStudentProfile from "../../components/StudentDetails/MultipleStudentProfiles";
import ErrorSnackbar from "../../widgets/Snackbar/ErrorSnackbar";
import Loader from "../../components/Loader/Loader";
import routes from "../../constants/routes";
import { CONSTANTS } from "../../constants/constants";
import tokenUtils from "../../utils/tokenUtils";
import { useSearchUser } from "../../hooks/useSearchUser";
import searchUserResponseAdapter from "../../adapters/searchUserAdapter";
import filterProfilesByVertical from "../../utils/filterProfilesByVertical";
import styles from "../../components/StudentDetails/StudentDetails.module.scss";

const PHONE_SECRET = process.env.REACT_APP_PHONE_SECRET || "il-support-secret";

const decryptPhone = (encryptedPhone) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedPhone, PHONE_SECRET);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    console.error("Failed to decrypt phone number");
    return "";
  }
};

const StudentDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const { mutateAsync } = useSearchUser();

  const [profilesModal, setProfilesModal] = useState({
    open: false,
    profiles: [],
  });

  const stateStudentData = location.state?.studentData || null;
  const statePhone = location.state?.phone || null;
  const stateBranchId = location.state?.branch_id || "";
  const stateBranchName = location.state?.branch_name || "";

  const vertical = location.state?.vertical || searchParams.get("vertical");
  const verticalInfo = VerticalRegistry.getVerticalByName(vertical);
  const features = verticalInfo?.features;
  const adminId = tokenUtils.getUserDetails()?.userId;

  const urlPhoneEncrypted = searchParams.get("phone");
  const urlUamId = searchParams.get("uam_id");
  const urlAdmissionNumber = searchParams.get("admission_number");

  const decryptedPhone = urlPhoneEncrypted
    ? decryptPhone(urlPhoneEncrypted)
    : "";

  const shouldFetch =
    !stateStudentData &&
    !!vertical &&
    (!!decryptedPhone || !!urlUamId || !!urlAdmissionNumber);

  const {
    data: queryResult,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "studentDetails",
      vertical,
      decryptedPhone,
      urlUamId,
      urlAdmissionNumber,
    ],
    queryFn: () =>
      mutateAsync({
        vertical,
        searchParams: {
          phone: decryptedPhone,
          uam_id: urlUamId,
          admission_number: urlAdmissionNumber,
        },
      }),
    enabled: shouldFetch,
    retry: false,
  });

  const { localStudentData, localPhone } = React.useMemo(() => {
    if (stateStudentData) {
      return { localStudentData: stateStudentData, localPhone: statePhone };
    }

    if (!queryResult?.data?.accounts) {
      return { localStudentData: null, localPhone: decryptedPhone };
    }

    
    const matchedProfiles = filterProfilesByVertical(
      queryResult.data.accounts,
      verticalInfo?.id,
      {
        uam_id: urlUamId,
        admission_number: urlAdmissionNumber,
      },
    );

    if (matchedProfiles.length === CONSTANTS.ONE) {
      return {
        localStudentData: searchUserResponseAdapter(
          matchedProfiles[0].account,
          matchedProfiles[0].profile,
        ),
        localPhone: matchedProfiles[0].account.phone || decryptedPhone,
      };
    }

    if (matchedProfiles.length > CONSTANTS.ONE && !profilesModal.open) {
      setTimeout(
        () => setProfilesModal({ open: true, profiles: matchedProfiles }),
        CONSTANTS.ZERO,
      );
    }

    return { localStudentData: null, localPhone: decryptedPhone };
  }, [queryResult, stateStudentData, statePhone]);

  const handleProfileSelect = ({ account, profile }) => {
    navigate(location.pathname + location.search, {
      state: {
        studentData: searchUserResponseAdapter(account, profile),
        phone: account.phone || localPhone,
        vertical,
      },
      replace: true,
    });
    setProfilesModal({ open: false, profiles: [] });
  };

  const errorMessage =
    error?.message ||
    (!isLoading && !localStudentData && !profilesModal.open && shouldFetch
      ? "No user found with the provided details for the selected vertical."
      : "") ||
    (!vertical && !stateStudentData ? "Vertical is missing in URL." : "");

  const showOtpCard =
    features?.getOtp || features?.resetOtp || features?.unblock;
  const hasRightColumn =
    features?.call || showOtpCard || features?.orderManagement;

  if (isLoading) return <Loader fullScreen />;

  if (!localStudentData && !profilesModal.open) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.headerBox}>
          <SecondaryButton
            onClick={() => navigate(routes.home.root)}
            sx={{ height: "36px", minWidth: "80px" }}
          >
            Go Back
          </SecondaryButton>
        </div>
        <p className={styles.noData}>No student data found.</p>
        <ErrorSnackbar
          open={!!errorMessage}
          message={errorMessage}
          onClose={() => {}}
        />
      </div>
    );
  }

  return (
    <>
      {localStudentData && (
        <div className={styles.wrapper}>
          <div className={styles.headerBox}>
            <h2 className={styles.title}>Student Details</h2>
            <div className={styles.badge}>
              {vertical || "Vertical Not Selected"}
            </div>
            <div className={styles.headerSpacer} />
            <SecondaryButton
              onClick={() => navigate(routes.home.root)}
              sx={{ height: "36px", minWidth: "80px" }}
            >
              Back
            </SecondaryButton>
          </div>

          <div
            className={`${styles.contentGrid} ${!hasRightColumn ? styles.singleColumn : ""}`}
          >
            <div className={styles.leftColumn}>
              <StudentInfoCard
                studentData={localStudentData}
                phone={localPhone}
                features={features}
                verticalInfo={verticalInfo}
              />
            </div>

            {hasRightColumn && (
              <div className={styles.rightColumn}>
                {features.call && (
                  <CallManagementCard
                    uamId={localStudentData?.uam_id}
                    adminId={adminId}
                  />
                )}
                {showOtpCard && (
                  <OtpManagementCard
                    phone={localPhone}
                    features={features}
                    studentData={localStudentData}
                    verticalInfo={verticalInfo}
                    branchId={stateBranchId}
                    branchName={stateBranchName}
                  />
                )}
                {features.orderManagement && (
                  <OrderManagementCard
                    uamId={localStudentData?.uam_id}
                    phone={localPhone}
                    vertical={vertical}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <MultipleStudentProfile
        open={profilesModal.open}
        onClose={() => {
          setProfilesModal({ open: false, profiles: [] });
          if (!localStudentData) navigate(routes.home.root);
        }}
        profiles={profilesModal.profiles}
        onSelectProfile={handleProfileSelect}
      />

      <ErrorSnackbar
        open={!!errorMessage}
        message={errorMessage}
        onClose={() => {}}
      />
    </>
  );
};

export default StudentDetails;
